import { createFileRoute, Link } from "@tanstack/react-router";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { GpsDriveSessionBanner } from "@/components/gpsdrive-session-banner";
import { useAdminBranding, useMyPermissions } from "@/hooks/queries";
import { useAnyPermission } from "@/hooks/use-permission";
import type { AdminBrandingSettings } from "@/services/gpsdrive-admin-branding.service";
import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";

export const Route = createFileRoute("/admin/")({
  component: AdminHubPage,
  head: () => ({ meta: [{ title: "Administração — Wp Rastreamento" }] }),
});

interface AdminCard {
  title: string;
  description: string;
  href: string;
}

interface AdminSection {
  number: string;
  title: string;
  description: string;
  action: string;
  cards: AdminCard[];
  comingSoon?: boolean;
}

const SECTIONS: AdminSection[] = [
  {
    number: "01",
    title: "Identidade Visual",
    description:
      "Personalize a aparência da sua plataforma, incluindo tema, cores, logotipos e tela de login.",
    action: "identidade_visual",
    cards: [
      {
        title: "Identidade Visual",
        description:
          "Tema & cor, tela de login, logotipos e dados de contato, com pré-visualização web e app.",
        href: "/admin/identity",
      },
    ],
  },
  {
    number: "02",
    title: "Conta & Integrações",
    description: "Serviços externos e documentos contratuais da empresa.",
    action: "conta_integracoes",
    cards: [
      {
        title: "Integrações",
        description: "Conectar e gerenciar serviços externos utilizados pela empresa.",
        href: "/admin/integrations",
      },
      {
        title: "Webhook & API",
        description:
          "Receber posições (ex.: tags) de outras plataformas e enviar alertas para o sistema da empresa.",
        href: "/admin/webhooks",
      },
      {
        title: "Contrato",
        description: "Criar, editar e gerenciar modelos de contratos e termos de serviço.",
        href: "/admin/contract",
      },
      {
        title: "Planos",
        description: "Nome, valor e descrição dos planos usados nas recorrências.",
        href: "/admin/plans",
      },
      {
        title: "Comandos GT06",
        description:
          "Catálogo de comandos GT06 (texto + senha) usados como atalho ao enviar comando.",
        href: "/admin/gt06-commands",
      },
      {
        title: "Senha para liberar sem teste",
        description:
          "Senha pedida em Agenda → Chamados para liberar o técnico a encerrar sem testar o rastreador (teste feito fora do sistema).",
        href: "/admin/technician-test-password",
      },
    ],
  },
  {
    number: "03",
    title: "Comunicação",
    description: "Mensagens enviadas aos clientes.",
    action: "comunicacao",
    cards: [
      {
        title: "Mensagem de Boas-Vindas",
        description: "Configurar a mensagem exibida para novos usuários/clientes.",
        href: "/admin/communication",
      },
      {
        title: "Configurar Assistência",
        description: "Tipos de serviço e regra de duplicidade para solicitações de assistência.",
        href: "/admin/assistance",
      },
      {
        title: "Falha de Comunicação",
        description:
          "Avisar o cliente por WhatsApp quando o veículo dele ficar muito tempo offline.",
        href: "/admin/communication-failure",
      },
      {
        title: "Corte de Energia",
        description:
          "Avisar o cliente e as pessoas autorizadas por WhatsApp quando o rastreador informar corte de energia ou bateria violada.",
        href: "/admin/power-cut-alert",
      },
      {
        title: "Mensagens de Agendamento",
        description:
          "WhatsApp para o cliente da Agenda no novo agendamento, no dia e no encerramento, com botões ou lista; as respostas ficam no agendamento.",
        href: "/admin/agenda-messages",
      },
    ],
  },
  {
    number: "04",
    title: "Formulários",
    description: "Formulários com o visual da empresa para o cliente preencher por um link.",
    action: "formularios",
    cards: [
      {
        title: "Formulários",
        description:
          "Crie cadastros e pesquisas com cor, banner e perguntas obrigatórias; veja as respostas.",
        href: "/admin/forms",
      },
    ],
  },
  {
    number: "05",
    title: "Módulos",
    description: "Módulos adicionais da plataforma.",
    action: "notificacoes",
    cards: [
      {
        title: "Gestão de Frota",
        description: "Módulo de gestão de frota da empresa.",
        href: "/admin/fleet-management",
      },
      {
        title: "Envio de Faturas pelo WhatsApp",
        description:
          "Lembrete antes do vencimento, no dia e cobrança depois de vencida, nos dias e horários escolhidos.",
        href: "/admin/invoice-whatsapp",
      },
      {
        title: "Assistente IA (Beta)",
        description:
          "Robô que conversa com os clientes no app: chaves do ChatGPT/Gemini, apresentação da empresa e funções com prompts próprios. Em teste.",
        href: "/admin/ai-assistant",
      },
      {
        title: "Assistente IA (Beta)",
        description:
          "Robô que conversa com os clientes no app: chaves do ChatGPT/Gemini, apresentação da empresa e um prompt por função (agenda, gastos, nutrição, imagens…).",
        href: "/admin/ai-assistant",
      },
    ],
  },
];

type ProgressStatus = "concluido" | "pendente" | "opcional";

function statusBadge(status: ProgressStatus) {
  if (status === "concluido") {
    return (
      <Badge className="border-transparent bg-emerald-500 text-white hover:bg-emerald-500">
        Concluído
      </Badge>
    );
  }
  if (status === "opcional") {
    return <Badge variant="secondary">Opcional</Badge>;
  }
  return (
    <Badge className="border-transparent bg-amber-500 text-white hover:bg-amber-500">
      Pendente
    </Badge>
  );
}

function identityProgress(
  branding: AdminBrandingSettings | undefined,
): { label: string; status: ProgressStatus }[] {
  return [
    { label: "Tema & Cor", status: branding?.primaryColor ? "concluido" : "pendente" },
    {
      label: "Tela de Login",
      status: branding?.loginBackgroundColor && branding?.loginTextColor ? "concluido" : "opcional",
    },
    { label: "Logotipos", status: branding?.logoUrl ? "concluido" : "opcional" },
    { label: "Dados & Contato", status: branding?.serverName ? "concluido" : "pendente" },
  ];
}

function SectionCard({ card, comingSoon }: { card: AdminCard; comingSoon?: boolean }) {
  return (
    <Link to={card.href}>
      <Card className="h-full transition-colors hover:border-primary/40 hover:bg-accent/40">
        <CardHeader>
          <div className="flex items-center gap-2">
            <CardTitle className="text-base">{card.title}</CardTitle>
            {comingSoon && <Badge variant="secondary">Em breve</Badge>}
          </div>
          <CardDescription>{card.description}</CardDescription>
        </CardHeader>
      </Card>
    </Link>
  );
}

function AdminHubPage() {
  const { data: branding } = useAdminBranding();
  // Bug real 2026-09-01 ("aparece sem permissões e depois de segundos
  // aparece"): cada VisibleSection checava sua própria permissão e
  // sumia enquanto useMyPermissions ainda carregava — a página nascia
  // incompleta (seções faltando) e ia "completando" sozinha alguns
  // segundos depois. Um loading único aqui evita isso: só renderiza as
  // seções depois que os dados de permissão realmente chegaram.
  const { data: permissions } = useMyPermissions();

  return (
    <div className="p-6 space-y-8">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Administração</h1>
        <p className="text-sm text-muted-foreground">
          Centraliza as configurações administrativas e de personalização da sua empresa de
          rastreamento.
        </p>
      </div>

      <GpsDriveSessionBanner />

      {!permissions ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        SECTIONS.map((section) => (
          <VisibleSection key={section.number} section={section} branding={branding} />
        ))
      )}
    </div>
  );
}

function VisibleSection({
  section,
  branding,
}: {
  section: AdminSection;
  branding: AdminBrandingSettings | undefined;
}) {
  const canSee = useAnyPermission("administracao", [section.action]);
  if (!canSee) return null;

  const isIdentity = section.number === "01";

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold tracking-tight">
          {section.number} — {section.title}
        </h2>
        <p className="text-sm text-muted-foreground">{section.description}</p>
      </div>

      <div className={cn("grid gap-4", isIdentity && "lg:grid-cols-[1fr_280px]")}>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {section.cards.map((card) => (
            <SectionCard key={card.title} card={card} comingSoon={section.comingSoon} />
          ))}
        </div>

        {isIdentity && (
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Progresso</CardTitle>
              <CardDescription>Status das configurações de identidade visual.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {identityProgress(branding).map((item) => (
                <div key={item.label} className="flex items-center justify-between gap-2">
                  <span className="text-sm">{item.label}</span>
                  {statusBadge(item.status)}
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    </section>
  );
}
